import React, { useState } from 'react';
import { api } from '../../../utils/api';
import entityRelationService from '../../../services/entityRelationService';

const TYPES = [
  'Customer', 'Lead', 'Opportunity', 'Activity', 'SaleNote',
  'Salesperson', 'Contract', 'Invoice', 'SupportTicket', 'BusinessCard'
];

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      {children}
    </div>
  );
}

export default function EntityRelationsTest() {
  const [sourceEntityType, setSourceEntityType] = useState('Activity');
  const [sourceEntityID, setSourceEntityID] = useState('');
  const [targetEntityType, setTargetEntityType] = useState('Customer');
  const [targetEntityID, setTargetEntityID] = useState('');
  const [relationType, setRelationType] = useState('related_to');
  const [relationID, setRelationID] = useState('');
  const [direction, setDirection] = useState('both');
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  const appendLog = (entry) => {
    setLog((prev) => [{ time: new Date().toISOString(), ...entry }, ...prev].slice(0, 200));
  };

  const safeNumber = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const doCreate = async () => {
    setLoading(true);
    try {
      const res = await entityRelationService.create({
        sourceEntityType,
        sourceEntityID: Number(sourceEntityID),
        targetEntityType,
        targetEntityID: Number(targetEntityID),
        relationType,
      });
      appendLog({ action: 'CREATE', ok: true, res });
    } catch (e) {
      appendLog({ action: 'CREATE', ok: false, error: e.message || e.toString() });
    } finally {
      setLoading(false);
    }
  };

  const doGet = async () => {
    setLoading(true);
    try {
      const res = await entityRelationService.getRelatedEntities(
        sourceEntityType,
        Number(sourceEntityID),
        { direction }
      );
      appendLog({ action: 'GET', ok: true, res });
    } catch (e) {
      appendLog({ action: 'GET', ok: false, error: e.message || e.toString() });
    } finally {
      setLoading(false);
    }
  };

  const doGetWithData = async () => {
    setLoading(true);
    try {
      const res = await entityRelationService.getRelatedEntitiesByType(
        sourceEntityType,
        Number(sourceEntityID),
        targetEntityType,
        { relationType }
      );
      appendLog({ action: 'GET with-data', ok: true, res });
    } catch (e) {
      appendLog({ action: 'GET with-data', ok: false, error: e.message || e.toString() });
    } finally {
      setLoading(false);
    }
  };

  const doByTarget = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/entityrelations/by-target/${targetEntityType}/${Number(targetEntityID)}/${sourceEntityType}`);
      appendLog({ action: 'GET by-target', ok: true, res });
    } catch (e) {
      appendLog({ action: 'GET by-target', ok: false, error: e.message || e.toString() });
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    const rid = safeNumber(relationID);
    if (!rid) {
      appendLog({ action: 'DELETE', ok: false, error: 'RelationID required' });
      return;
    }
    setLoading(true);
    try {
      const res = await entityRelationService.delete(rid);
      appendLog({ action: 'DELETE', ok: true, res });
    } catch (e) {
      appendLog({ action: 'DELETE', ok: false, error: e.message || e.toString() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relationships API Tester</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">All calls send Authorization and X-Tenant-Code headers via the shared api utility.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Source Entity Type">
              <select value={sourceEntityType} onChange={(e) => setSourceEntityType(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800">
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Source Entity ID">
              <input value={sourceEntityID} onChange={(e) => setSourceEntityID(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" placeholder="e.g. 7" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Target Entity Type">
              <select value={targetEntityType} onChange={(e) => setTargetEntityType(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800">
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Target Entity ID">
              <input value={targetEntityID} onChange={(e) => setTargetEntityID(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" placeholder="e.g. 13" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Relation Type">
              <input value={relationType} onChange={(e) => setRelationType(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" placeholder="related_to" />
            </Field>
            <Field label="Direction (GET)">
              <select value={direction} onChange={(e) => setDirection(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800">
                <option value="source">source</option>
                <option value="target">target</option>
                <option value="both">both</option>
              </select>
            </Field>
            <Field label="Relation ID (DELETE)">
              <input value={relationID} onChange={(e) => setRelationID(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" placeholder="e.g. 101" />
            </Field>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={doCreate} disabled={loading} className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Create</button>
            <button onClick={doGet} disabled={loading} className="px-3 py-2 bg-gray-700 text-white rounded disabled:opacity-50">GET /:entityType/:entityID</button>
            <button onClick={doGetWithData} disabled={loading} className="px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">GET with-data</button>
            <button onClick={doByTarget} disabled={loading} className="px-3 py-2 bg-emerald-600 text-white rounded disabled:opacity-50">GET by-target</button>
            <button onClick={doDelete} disabled={loading} className="px-3 py-2 bg-red-600 text-white rounded disabled:opacity-50">Delete</button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Log</div>
          <div className="border rounded p-2 h-[520px] overflow-auto bg-white dark:bg-gray-900">
            {log.length === 0 ? (
              <div className="text-gray-400 text-sm">No entries yet.</div>
            ) : (
              <ul className="space-y-2">
                {log.map((l, idx) => (
                  <li key={idx} className="text-xs">
                    <div className="text-gray-500">[{l.time}] {l.action} {l.ok ? '✅' : '❌'}</div>
                    {l.error ? (
                      <pre className="whitespace-pre-wrap break-words text-red-400">{String(l.error)}</pre>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words text-gray-300">{JSON.stringify(l.res, null, 2)}</pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


